import React from "react";
import {Box, Flex, Text, Icon, Spinner} from "@chakra-ui/react";
import {FiFolder} from "react-icons/fi";
import Breadcrumb from "./Breadcrumb";
import FolderCard from "./FolderCard";
import "./fileCards.css";
import FileCard from "./FileCard";

function formatChildren(list = []) {
  const folders = list.filter((it) => it.name?.endsWith("/"));
  const files = list.filter((it) => !it.name?.endsWith("/"));
  return {folders, files};
}

export default function PatientFileBrowser({
  folderItems,
  currentPath,
  rootPath,
  onNavigate,
  isNavigating,
  dashState,
  setSelectedPath,
  setSelectedFile,
  selectedPath,
  selectedFile,
  setPreview,
  toast,
  selectedFiles = [],
  setSelectedFiles,
  searchQuery,
  searchResults = [],
  isSearching = false,
  onSearchResultClick,
}) {
  const {folders, files} = formatChildren(folderItems);
  const showSearchMode = searchQuery.trim().length > 0;
  const searchFolders = searchResults.filter(
    (result) => result.isFolder || result.path.endsWith("/")
  );
  const searchFiles = searchResults.filter(
    (result) => !(result.isFolder || result.path.endsWith("/"))
  );
  const hasContent = folders.length > 0 || files.length > 0;
  const getFileLocation = (fullPath) => {
    const separatorIndex = fullPath.lastIndexOf("/");
    if (separatorIndex === -1) {
      return {basePath: "", fileName: fullPath};
    }
    return {
      basePath: fullPath.substring(0, separatorIndex + 1),
      fileName: fullPath.substring(separatorIndex + 1),
    };
  };
  const getResultContextPath = (fullPath, itemName) => {
    const normalizedRoot = rootPath || "";
    const relativePath = fullPath.startsWith(normalizedRoot)
      ? fullPath.slice(normalizedRoot.length)
      : fullPath;
    const parentPath = relativePath.endsWith(itemName)
      ? relativePath.slice(0, -itemName.length)
      : relativePath;
    const cleaned = parentPath.replace(/\/$/, "");
    return cleaned ? cleaned.split("/").filter(Boolean).join(" / ") : "Root";
  };
  const displayFolders = showSearchMode
    ? searchFolders.map((result) => ({
        key: result.path,
        name: result.name,
        contextPath: getResultContextPath(result.path, result.name),
        onNavigate: () => onSearchResultClick?.(result),
      }))
    : folders.map((folder) => ({
        key: folder.name,
        name: folder.name,
        contextPath: "",
        onNavigate: (folderName) => onNavigate(currentPath + folderName),
      }));
  const displayFiles = showSearchMode
    ? searchFiles.map((result) => {
        const {basePath, fileName} = getFileLocation(result.path);
        const displayName = result.name || fileName;
        return {
          key: result.path,
          basePath,
          name: displayName,
          storedDisplayName: result.displayName,
          storedAddedAt: result.addedAt,
          storedLastModified: result.lastModified,
          contextPath: getResultContextPath(result.path, displayName),
        };
      })
    : files.map((file) => ({
        key: file.name,
        basePath: currentPath,
        name: file.name,
        storedDisplayName: file.displayName,
        storedAddedAt: file.addedAt,
        storedLastModified: file.lastModified,
        contextPath: "",
      }));
  const hasDisplayContent = displayFolders.length > 0 || displayFiles.length > 0;
  const showLoadingState = showSearchMode
    ? isSearching && !hasDisplayContent
    : isNavigating;
  const showEmptyState = showSearchMode
    ? !isSearching && !hasDisplayContent
    : !hasContent;

  return (
    <Box>
      <Breadcrumb
        currentPath={currentPath}
        rootPath={rootPath}
        onNavigate={onNavigate}
      />

      {showLoadingState ? (
        <Flex justify="center" align="center" h="200px">
          <Spinner size="sm" color="blue.400" mr={3} />
          <Text fontSize="13px" color="gray.500">
            {showSearchMode ? "Searching..." : "Loading..."}
          </Text>
        </Flex>
      ) : showEmptyState ? (
        <Flex
          justify="center"
          align="center"
          h="200px"
          direction="column"
          className="folder-content-appear"
        >
          <Icon as={FiFolder} fontSize="36px" color="gray.300" mb={3} />
          <Text color="gray.400" fontSize="13px">
            {showSearchMode
              ? `No files or folders found matching "${searchQuery}"`
              : "This folder is empty"}
          </Text>
        </Flex>
      ) : (
        <Box className="folder-content-appear">
          {/* Folders grid */}
          {displayFolders.length > 0 && (
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(140px, 150px))"
              gap={3}
              px={2}
              py={showSearchMode ? 2 : 0}
              pb={displayFiles.length > 0 ? 4 : 2}
            >
              {displayFolders.map((folder) => (
                <FolderCard
                  key={folder.key}
                  name={folder.name}
                  onNavigate={folder.onNavigate}
                  contextPath={folder.contextPath}
                />
              ))}
            </Box>
          )}

          {/* Files grid */}
          {displayFiles.length > 0 && (
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(140px, 150px))"
              gap={3}
              px={2}
              py={showSearchMode ? 2 : 0}
              pb={2}
            >
              {displayFiles.map((file) => (
                <FileCard
                  key={file.key}
                  dashState={dashState}
                  basePath={file.basePath}
                  name={file.name}
                  storedDisplayName={file.storedDisplayName}
                  storedAddedAt={file.storedAddedAt}
                  storedLastModified={file.storedLastModified}
                  contextPath={file.contextPath}
                  setSelectedPath={setSelectedPath}
                  setSelectedFile={setSelectedFile}
                  selectedPath={selectedPath}
                  selectedFile={selectedFile}
                  setPreview={setPreview}
                  toast={toast}
                  selectedFiles={selectedFiles}
                  setSelectedFiles={setSelectedFiles}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
